import * as React from "react"
import { toast } from "sonner"

import { useTranslations } from "@/i18n"
import { resolveApiErrorMessage } from "@/shared/lib/api-errors/api-error"

/**
 * Returns a memoized resolver that turns any thrown value into a localized, user-facing message.
 * Use for non-toast surfaces (banners, inline text). Pass the feature error namespace as `domain`
 * (e.g. `"finance"`) so domain codes resolve via `<domain>.errors.<CODE>` before the shared generics.
 */
export function useApiErrorMessage(domain?: string) {
  // Un-namespaced root translator so the resolver can read any namespace it needs.
  const t = useTranslations()
  return React.useCallback((error: unknown) => resolveApiErrorMessage(error, t, { domain }), [t, domain])
}

/**
 * Returns a memoized `(error) => void` that resolves a localized message and shows it via toast.
 * This is the primary call-site API for catch blocks. It NEVER surfaces a raw backend string:
 *
 *   const toastError = useApiErrorToast("finance")
 *   try { ... } catch (error) { toastError(error) }
 */
export function useApiErrorToast(domain?: string) {
  const resolve = useApiErrorMessage(domain)
  return React.useCallback((error: unknown) => toast.error(resolve(error)), [resolve])
}
